import { CircleLoader } from "@/components/shared/loaders";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { BidDecisionGroup, DecisionEnum, Service } from "@/graphql/__generated__/graphql";
import { useRequestMarginDecisionOnTenderMutationHook } from "@/hooks";
import { Dispatch, JSX, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * Function to get bidding groups that has the decison of BID without service
 * @param services - List of services
 * @param biddingGroups - List of bidding groups
 * @returns
 */
function getBiddingGroupsWithoutService(
  services: Service[],
  biddingGroups: Partial<BidDecisionGroup>[]
) {
  return biddingGroups.filter(
    (group: Partial<BidDecisionGroup>) =>
      group.currentDecision === DecisionEnum.Bid &&
      !services.some(
        (service: Service) =>
          service.operationalOrganizationId === group.operationalOrganizationId
      )
  );
}

interface Props {
  services: Service[];
  biddingGroups: Partial<BidDecisionGroup>[];
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

/**
 * MarginDecisionAlertDialog component renders an alert dialog to confirm the user's decision.
 *
 * @param {Props} props - The properties passed to the component.
 * @param {boolean} props.open - Determines if the alert dialog is open.
 * @param {function} props.onOpenChange - Callback function to handle the change in the open state of the dialog.
 *
 * @returns {JSX.Element} The rendered alert dialog component.
 */
const MarginDecisionAlertDialog = (props: Props): JSX.Element => {

  const { services, biddingGroups, open, onOpenChange } = props;

  // Hooks
  const { t } = useTranslation(["common", "modals"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Mutation hooks
  const { requestMarginDecisionOnTender, loading } = useRequestMarginDecisionOnTenderMutationHook();

  // Constants
  const groupsWithoutService = getBiddingGroupsWithoutService(services, biddingGroups);
  const canRequest = groupsWithoutService.length === 0;

  /**
   * Handler for the submit button
   *
   * Fires the requestMarginDecisionMutation to request a margin decision approval.
   *
   * @returns {void}
   */
  const onSubmit = (): void => {
    try {
      requestMarginDecisionOnTender({
        variables: {
          tenderId: Number(tenderID),
        }
      });
    } catch (error) {
      console.log("Error requesting margin decision", error);
    } finally {
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="select-none">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {canRequest ? t("modals:biddecision.title") : t("modals:marginrequest.requestapprovalunavailable")}
          </AlertDialogTitle>
          {
            canRequest ? (
              <>
                <AlertDialogDescription>
                  {t("modals:marginrequest.firstdisclaimer")}
                </AlertDialogDescription>
                <AlertDialogDescription>
                  {t("modals:marginrequest.seconddisclaimer")}
                </AlertDialogDescription>
              </>
            ) : (
              <>
                <AlertDialogDescription>
                  {t("modals:marginrequest.servicerequireddescription")}
                </AlertDialogDescription>
                <div>
                  <AlertDialogDescription>
                    {t("modals:marginrequest.unassignedorganizationdescription")}
                  </AlertDialogDescription>
                  <ul className="text-sm pt-2 font-semibold text-slate-800">
                    {
                      groupsWithoutService.map((group) => (
                        <li key={group.id}>{group.operationalOrganizationShortName}</li>
                      ))
                    }
                  </ul>
                </div>
              </>
            )
          }
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus={false} onKeyDown={(e) => e.preventDefault()}>
            {t("buttons.cancel")}
          </AlertDialogCancel>
          <Button
            autoFocus={false}
            className={cn("gap-2", loading && "cursor-wait")}
            type={loading ? "button" : "submit"}
            form="tender-decision-form"
            disabled={loading || !canRequest}
            onClick={onSubmit}
            onKeyDown={(e) => e.preventDefault()}
          >
            <CircleLoader visible={loading} height={16} width={16} strokeWidth={2}/>
            {loading? (
              t("buttons.submitting")
            ) : (
              t("buttons.submit")
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default MarginDecisionAlertDialog;
